import React, { useMemo } from 'react';
import { getCellStyle } from '../../utils/GIPlanningUtils';

const GIDetailedPlanningTable = ({
  isMobile = false,
  detailedViewMode,
  detailedData,
  interventionPointsData,
  detailedDates,
  currentDate,
  handleCellClick,
  selectedCampaign
}) => {
  const currentData = detailedViewMode === 'promoters' ? detailedData : interventionPointsData;
  const headerLabel = detailedViewMode === 'promoters' ? 'Promoteur' : 'Point d\'intervention';
  const headerSlotStats = useMemo(() => {
    if (detailedViewMode !== 'promoters') return new Map();

    const statsByDate = new Map();

    detailedDates.forEach((date) => {
      let animatedSlots = 0;
      let totalWithoutCancelled = 0;

      detailedData.forEach((promoter) => {
        const dateStates = promoter.dateStates?.get(date);
        if (!dateStates) return;

        dateStates.forEach((count, state) => {
          if (state === 'Animé') {
            animatedSlots += count;
          }

          if (state !== 'Annulé') {
            totalWithoutCancelled += count;
          }
        });
      });

      const percentage = totalWithoutCancelled > 0
        ? Math.round((animatedSlots / totalWithoutCancelled) * 100)
        : null;

      statsByDate.set(date, {
        animatedSlots,
        totalWithoutCancelled,
        percentage
      });
    });

    return statsByDate;
  }, [detailedData, detailedDates, detailedViewMode]);

  return (
    <div className={`border border-gray-200 dark:border-gray-600 ${isMobile ? 'rounded-none border-l-0 border-r-0' : 'rounded-lg'} overflow-hidden`}>
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="sticky left-0 top-0 z-30 bg-gray-50 dark:bg-gray-700 border-r border-b border-gray-200 dark:border-gray-600 px-2 md:px-3 py-1 md:py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider w-64 md:w-96 min-w-64 md:min-w-96">
                {headerLabel}
              </th>
              {detailedDates.map(date => (
                <th key={`header-${date}`} className={`sticky top-0 z-10 border-r border-b border-gray-200 dark:border-gray-600 px-1 md:px-2 py-1 md:py-2 text-center text-xs font-medium uppercase tracking-wider ${
                  date === currentDate
                    ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200'
                    : 'bg-gray-50 dark:bg-gray-700 text-gray-500 dark:text-gray-300'
                }`}>
                <div>
                  <div className="font-semibold">
                    {new Date(date).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit'
                    })}
                  </div>
                  {date === currentDate && (
                    <div className="text-[10px] text-blue-600 dark:text-blue-300 font-medium">
                      Aujourd'hui
                    </div>
                  )}
                  {detailedViewMode === 'promoters' && (() => {
                    const slotStats = headerSlotStats.get(date);
                    if (!slotStats) return null;

                    return (
                      <div className="mt-1 text-[10px] normal-case leading-tight text-gray-600 dark:text-gray-300">
                        <div>
                          {slotStats.animatedSlots} / {slotStats.totalWithoutCancelled}
                        </div>
                        <div>
                          {slotStats.percentage !== null ? `${slotStats.percentage}%` : '-'}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="bg-white dark:bg-gray-800">
          {currentData.map((item, index) => (
            <tr key={detailedViewMode === 'promoters' ? `promoter-${item.promoterId}` : `point-${item.interventionPoint}`} className="group hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <td className="sticky left-0 z-20 bg-white dark:bg-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-700 border-r border-gray-200 dark:border-gray-600 px-2 md:px-3 py-1 md:py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                <div className="truncate" title={detailedViewMode === 'promoters' ? item.promoter : item.interventionPoint}>
                  {detailedViewMode === 'promoters' ? (
                    <>
                      {item.promoter} <span className="text-gray-500 dark:text-gray-400"> ({item.dates.length})</span>
                    </>
                  ) : (
                    <>
                      {item.interventionPoint} <span className="text-gray-500 dark:text-gray-400">({item.dates.length})</span>
                    </>
                  )}
                </div>
              </td>
              {detailedDates.map(date => (
                <td key={`${detailedViewMode === 'promoters' ? item.promoterId : item.interventionPoint}-${date}`} className={`border-r border-gray-200 dark:border-gray-600 px-1 md:px-2 py-1 md:py-2 text-center ${
                  date === currentDate ? 'bg-blue-50 dark:bg-blue-950' : ''
                }`}>
                    {item.dates.includes(date) ? (
                    <div
                      className={`py-1 px-2 rounded text-xs font-medium cursor-pointer hover:opacity-80 ${
                        date === currentDate
                          ? 'bg-blue-200 dark:bg-blue-800 text-blue-900 dark:text-blue-100'
                          : (() => {
                              const dateStates = item.dateStates?.get(date);
                              const cellStyle = getCellStyle(dateStates);
                              return cellStyle.className;
                            })()
                      }`}
                      onClick={() => handleCellClick(
                        selectedCampaign,
                        date,
                        detailedViewMode === 'promoters' ? 'promoter' : 'interventionPoint',
                        detailedViewMode === 'promoters' ? item.promoterId : item.interventionPoint
                      )}
                      title="Cliquez pour voir les détails"
                    >
                      {(() => {
                        const dateStates = item.dateStates?.get(date);
                        const cellStyle = getCellStyle(dateStates);
                        return cellStyle.icon;
                      })()}
                    </div>
                  ) : (
                    <div className="text-gray-300 dark:text-gray-600">-</div>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
};

export default GIDetailedPlanningTable;